<script lang="ts">
  import { enregistrer } from '../donnees/personnages'
  import { aller } from '../routage'
  import { creerPersonnage } from '../rules'
  import { creerAtelier, ETAPES, type EtapeId } from '../stores/creation.svelte'
  import Caracteristiques from './etapes/Caracteristiques.svelte'
  import Classe from './etapes/Classe.svelte'
  import Competences from './etapes/Competences.svelte'
  import Equipement from './etapes/Equipement.svelte'
  import Maitrises from './etapes/Maitrises.svelte'
  import Recapitulatif from './etapes/Recapitulatif.svelte'
  import Region from './etapes/Region.svelte'

  const atelier = creerAtelier()

  /**
   * Le nombre d'anomalies par étape : le joueur doit voir où ça coince sans
   * avoir à parcourir les sept pages.
   */
  const alertes = $derived(
    Object.fromEntries(
      ETAPES.map((e) => [e.id, atelier.anomaliesDe(e.id as EtapeId).length]),
    ),
  )

  const index = $derived(ETAPES.findIndex((e) => e.id === atelier.etape))

  /** Ce que le domaine a répondu si la Création a été refusée au dernier moment. */
  let refus = $state<string[]>([])

  /**
   * Fait aboutir la feuille et range le personnage.
   *
   * Le bouton n'est proposé que si le domaine dit la feuille créable, mais on
   * redemande quand même : c'est lui qui tranche, pas l'écran.
   */
  async function creer() {
    const verdict = creerPersonnage(atelier.feuille)
    if (verdict.verdict === 'refuse') {
      refus = verdict.anomalies.map((a) => a.message)
      return
    }
    const range = await enregistrer(verdict.personnage)
    atelier.recommencer()
    aller(`/fiche/${range.id}`)
  }

  /**
   * Ce qui cloche sur l'étape affichée. La pastille dit qu'il y a quelque
   * chose ; encore faut-il dire quoi, sans faire aller jusqu'au récapitulatif.
   * Celui-ci les liste déjà toutes : inutile de les répéter là-bas.
   */
  const aCorriger = $derived(
    atelier.etape === 'histoire' ? [] : atelier.anomaliesDe(atelier.etape),
  )
</script>

<header>
  <button type="button" class="lien retour" onclick={() => aller('/')}>
    ← Mes personnages
  </button>
  <h1>Création de personnage</h1>
  <button type="button" class="lien" onclick={() => atelier.recommencer()}>
    Tout recommencer
  </button>
</header>

<nav aria-label="Étapes de la Création">
  <div class="onglets" role="tablist">
    {#each ETAPES as e, i (e.id)}
    <button
      type="button"
      class="onglet"
      role="tab"
      aria-selected={e.id === atelier.etape}
      onclick={() => (atelier.etape = e.id as EtapeId)}
    >
      <span class="rang">{i + 1}</span>
      <span class="nom">{e.nom}</span>
        {#if (alertes[e.id] ?? 0) > 0}
          <span class="pastille" aria-label="{alertes[e.id] ?? 0} point(s) à corriger">
            {alertes[e.id] ?? 0}
          </span>
        {/if}
      </button>
    {/each}
  </div>
</nav>

{#if aCorriger.length > 0}
  <ul class="a-corriger">
    {#each aCorriger as a, i (a.message + i)}
      <li>{a.message}</li>
    {/each}
  </ul>
{/if}

<main>
  {#if atelier.etape === 'region'}
    <Region {atelier} />
  {:else if atelier.etape === 'classe'}
    <Classe {atelier} />
  {:else if atelier.etape === 'caracteristiques'}
    <Caracteristiques {atelier} />
  {:else if atelier.etape === 'competences'}
    <Competences {atelier} />
  {:else if atelier.etape === 'maitrises'}
    <Maitrises {atelier} />
  {:else if atelier.etape === 'equipement'}
    <Equipement {atelier} />
  {:else}
    <Recapitulatif {atelier} {creer} {refus} />
  {/if}
</main>

<footer>
  <button
    type="button"
    disabled={index <= 0}
    onclick={() => (atelier.etape = ETAPES[index - 1]!.id as EtapeId)}
  >
    Précédent
  </button>
  <span class="etat">
    {#if atelier.etat.creable}
      Prêt à créer
    {:else}
      {atelier.etat.anomalies.length} point{atelier.etat.anomalies.length > 1 ? 's' : ''} à corriger
    {/if}
  </span>
  <button
    type="button"
    class="suivant"
    disabled={index >= ETAPES.length - 1}
    onclick={() => (atelier.etape = ETAPES[index + 1]!.id as EtapeId)}
  >
    Suivant
  </button>
</footer>

<style>
  .retour {
    flex-basis: 100%;
  }
  header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: calc(var(--pas) * 3);
    flex-wrap: wrap;
    margin-bottom: calc(var(--pas) * 4);
  }
  h1 {
    font-family: 'Cinzel', serif;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: clamp(1.1rem, 4vw, 1.6rem);
    margin: 0;
    color: var(--accent);
  }
  .lien {
    background: none;
    border: none;
    color: var(--ink-soft);
    text-decoration: underline;
    cursor: pointer;
    font: inherit;
    font-size: 0.8rem;
  }

  /* `onglets` et `onglet` sont les onglets mobiles du design system. */
  nav {
    margin-bottom: calc(var(--pas) * 6);
  }
  .onglet {
    display: flex;
    align-items: center;
    gap: var(--pas);
  }
  .onglet:hover {
    color: var(--ink);
  }
  .rang {
    font-size: 0.7rem;
    opacity: 0.6;
  }
  /* Sur l'onglet actif, la pastille s'inverse : rouge sur rouge ne se lit pas. */
  .onglet[aria-selected='true'] .pastille {
    background: var(--accent-txt);
    color: var(--accent);
  }
  .pastille {
    font-family: system-ui, sans-serif;
    background: var(--ko);
    color: var(--accent-txt);
    border-radius: 999px;
    min-width: 1.15rem;
    height: 1.15rem;
    display: inline-grid;
    place-items: center;
    font-size: 0.65rem;
    font-variant-numeric: tabular-nums;
  }

  .a-corriger {
    list-style: none;
    margin: 0 0 calc(var(--pas) * 5);
    padding: calc(var(--pas) * 2) calc(var(--pas) * 4);
    display: grid;
    gap: var(--pas);
    background: var(--surface);
    border-left: 3px solid var(--ko);
    border-radius: var(--r);
    font-size: 0.85rem;
    color: var(--ink-soft);
  }

  main {
    min-height: 50vh;
  }

  footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc(var(--pas) * 3);
    margin-top: calc(var(--pas) * 10);
    padding-top: calc(var(--pas) * 4);
    border-top: 1px solid var(--line);
  }
  footer button {
    background: var(--surface-2);
    border: 1px solid var(--line-fort);
    border-radius: var(--r);
    color: var(--ink);
    min-height: 44px;
    padding: 0 calc(var(--pas) * 4);
    cursor: pointer;
    font: inherit;
    font-size: 0.85rem;
  }
  footer button.suivant {
    background: var(--accent);
    border-color: var(--accent);
    color: var(--accent-txt);
  }
  footer button:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .etat {
    font-size: 0.8rem;
    color: var(--ink-soft);
    text-align: center;
  }
</style>
